import {
  TABLE_CAPTION_CLASS,
  TABLE_CLASS,
  TABLE_FOOTNOTE_CLASS,
  TABLE_STYLE_CSS,
} from '../../utils/html-sanitization/bayer-vocabulary'

/**
 * Parse a raw HTML string containing a <table> into a 2D grid of cell text.
 * Returns null when there is no usable table (empty input, no rows, or no cells),
 * which callers treat as "no table content available".
 */
export function parseHtmlTableToValues(html: string): string[][] | null {
  if (!html.trim()) {
    return null
  }

  if (typeof DOMParser === 'undefined') {
    return null
  }

  const doc = new DOMParser().parseFromString(html, 'text/html')
  const rows = Array.from(doc.querySelectorAll('table tr'))

  if (rows.length === 0) {
    return null
  }

  const values = rows.map((row) =>
    Array.from(row.querySelectorAll('th, td')).map((cell) => (cell.textContent ?? '').trim())
  )

  const hasColumns = values.some((row) => row.length > 0)
  return hasColumns ? values : null
}

function normalizeCellText(value: string): string {
  return value.replace(/\s+/g, ' ').trim()
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
}

/**
 * Build a Bayer-styled HTML table from a 2D grid.
 * Added styles are approximation to Word tables from the template, but far from perfect.
 *
 * Structure:
 *   - Optional caption row  → <tr><td colspan="N"><p class="MsoCaption">title</p></td></tr>
 *   - Column-heading row    → <tr><td>…</td>…</tr>
 *   - Data rows             → <tr><td>…</td>…</tr>
 *   - Optional footer row   → <tr><td colspan="N"><p class="BayerTableFootnote">footer</p></td></tr>
 */
export function buildTableHtmlFromTableData(
  tableData: string[][],
  title?: string,
  footer?: string
): string {
  if (tableData.length === 0) return ''

  const [header, ...body] = tableData
  const colCount = header.length
  const rows: string[] = []

  const trimmedTitle = title?.trim()
  if (trimmedTitle) {
    rows.push(
      `<tr><td colspan="${colCount}" style="border-bottom:solid windowtext 1.5pt; padding:0cm 5.4pt 0cm 5.4pt;"><p class="${TABLE_CAPTION_CLASS}">${escapeHtml(trimmedTitle)}</p></td></tr>`
    )
  }

  rows.push(
    `<tr>${header
      .map(
        (cell) =>
          `<td style="border-bottom:solid windowtext 1.0pt;padding:0cm 5.4pt 0cm 5.4pt;height:1.0pt;">${escapeHtml(normalizeCellText(cell))}</td>`
      )
      .join('')}</tr>`
  )

  for (const row of body) {
    rows.push(
      `<tr>${row.map((cell) => `<td>${escapeHtml(normalizeCellText(cell))}</td>`).join('')}</tr>`
    )
  }

  const trimmedFooter = footer?.trim()
  if (trimmedFooter) {
    rows.push(
      `<tr><td colspan="${colCount}" style='border:none;border-top:solid windowtext 1.5pt;padding:
  0cm 5.4pt 0cm 5.4pt;'><p class="${TABLE_FOOTNOTE_CLASS}">${escapeHtml(trimmedFooter)}</p></td></tr>`
    )
  }

  return `<table class="${TABLE_CLASS}">${rows.join('')}</table>`
}

/**
 * Compose the full HTML fragment inserted into the document:
 * a Bayer-styled table preceded by the shared table stylesheet block.
 */
export function buildTableInsertHtml(params: {
  tableData: string[][]
  sourceTableHtml: string | null
  title: string
  footer: string
}): string {
  const { tableData, title, footer } = params
  // TODO: when current document flow is implemented, sourceTableHtml should be used to
  // preserve original Word markup/styling instead of building plain cells.
  // The current implementation is suitable only for filepool tables (without styles)

  return `<style>${TABLE_STYLE_CSS}</style>${buildTableHtmlFromTableData(tableData, title, footer)}`
}
